import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import Parchment from 'parchment';
import htmlEditButton from 'quill-html-edit-button';
import ImageResize from 'quill-image-resize-module-react';
import 'react-quill/dist/quill.snow.css';
import './RichDescriptionEditor.css';

/**
 * Ensures img tags have width/height as attributes so dimensions persist after save.
 * Converts inline style (e.g. style="width: 300px") to width/height attributes.
 */
export function preserveImageDimensions(html) {
  if (!html || typeof html !== 'string') return html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const imgs = doc.querySelectorAll('img');
  imgs.forEach((img) => {
    let w = img.getAttribute('width');
    let h = img.getAttribute('height');
    if (!w || !h) {
      const style = img.getAttribute('style') || '';
      const widthMatch = style.match(/width:\s*(\d+)px/);
      const heightMatch = style.match(/height:\s*(\d+)px/);
      if (!w && widthMatch) w = widthMatch[1];
      if (!h && heightMatch) h = heightMatch[1];
      if (!w && img.width) w = String(img.width);
      if (!h && img.height) h = String(img.height);
    }
    if (w) img.setAttribute('width', w);
    if (h) img.setAttribute('height', h);
  });
  return doc.body.innerHTML;
}

// Register modules once (Quill 1.x)
let modulesRegistered = false;
if (!modulesRegistered) {
  Quill.register('modules/htmlEditButton', htmlEditButton);
  Quill.register('modules/imageResize', ImageResize);
  modulesRegistered = true;
}

const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ font: [] }],
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  ['blockquote', 'code-block'],
  ['link', 'image', 'video'],
  ['clean'],
];

export default function RichDescriptionEditor({ value, onChange, placeholder, minHeight = 280, previewLabel }) {
  const [showPreview, setShowPreview] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlValue, setHtmlValue] = useState(value || '');

  const quillRef = useRef(null);
  const previewRef = useRef(null);

  const displayValue = htmlMode ? htmlValue : (value || '');

  // Mark portrait images in preview
  useEffect(() => {
    if (!showPreview || !previewRef.current) return;
    const container = previewRef.current;
    const content = container.querySelector('.rich-editor-preview-content');
    if (!content) return;
    const imgs = content.querySelectorAll('img');
    const checkPortrait = (img) => {
      if (img.naturalWidth && img.naturalHeight && img.naturalHeight > img.naturalWidth) {
        img.classList.add('img-portrait');
      }
    };
    imgs.forEach((img) => {
      if (img.complete) {
        checkPortrait(img);
      } else {
        img.addEventListener('load', () => checkPortrait(img));
      }
    });
  }, [showPreview, displayValue]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: TOOLBAR_OPTIONS,
      },
      htmlEditButton: {
        buttonHTML: '&lt;/&gt;',
        buttonTitle: 'Edit HTML source',
        msg: 'Edit the content in HTML format. When you click OK, the editor content will be replaced.',
        okText: 'OK',
        cancelText: 'Cancel',
      },
      imageResize: {
        parchment: Parchment,
        modules: ['Resize', 'DisplaySize', 'Toolbar'],
      },
    }),
    []
  );

  useEffect(() => {
    const el = quillRef.current;
    if (!el?.getEditor) return;
    const quill = el.getEditor();
    const root = quill.root;

    const handleDragOver = (e) => {
      if (e?.dataTransfer?.types?.includes('Files')) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleDrop = (e) => {
      const files = e?.dataTransfer?.files;
      if (!files?.length) return;
      const file = files[0];
      if (!file?.type?.startsWith('image/')) return;
      e.preventDefault();
      e.stopPropagation();
      const reader = new FileReader();
      reader.onload = (ev) => {
        const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
        quill.insertEmbed(range.index, 'image', ev.target.result, Quill.sources.USER);
        quill.setSelection(range.index + 1);
      };
      reader.readAsDataURL(file);
    };

    root.addEventListener('dragover', handleDragOver);
    root.addEventListener('drop', handleDrop);
    return () => {
      root.removeEventListener('dragover', handleDragOver);
      root.removeEventListener('drop', handleDrop);
    };
  }, []);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'script',
    'list', 'bullet', 'indent', 'align',
    'blockquote', 'code-block',
    'link', 'image', 'video',
  ];

  const refreshPreview = () => setPreviewKey((k) => k + 1);

  const enterHtmlMode = () => {
    const q = quillRef.current?.getEditor?.();
    const html = q ? q.root.innerHTML : (value || '');
    setHtmlValue(html);
    setHtmlMode(true);
  };

  const exitHtmlMode = () => {
    onChange(preserveImageDimensions(htmlValue));
    setHtmlMode(false);
  };

  return (
    <div className="rich-description-editor">
      <div className="rich-editor-toolbar-extra">
        <button
          type="button"
          className={`admin-btn admin-btn-secondary admin-btn-small ${htmlMode ? 'active' : ''}`}
          onClick={() => (htmlMode ? exitHtmlMode() : enterHtmlMode())}
          title={htmlMode ? 'Switch to visual editor' : 'Edit HTML source'}
        >
          {htmlMode ? 'Visual' : 'HTML'}
        </button>
        <button
          type="button"
          className={`admin-btn admin-btn-secondary admin-btn-small ${showPreview ? 'active' : ''}`}
          onClick={() => setShowPreview(!showPreview)}
          title={showPreview ? 'Hide preview' : 'Show preview'}
        >
          {showPreview ? 'Hide preview' : 'Preview'}
        </button>
        {showPreview && (
          <button
            type="button"
            className="admin-btn admin-btn-secondary admin-btn-small"
            onClick={refreshPreview}
            title="Refresh preview"
          >
            Refresh
          </button>
        )}
      </div>
      {htmlMode ? (
        <div className="rich-editor-html-mode">
          <textarea
            className="rich-editor-html-textarea"
            value={htmlValue}
            onChange={(e) => {
              const v = e.target.value;
              setHtmlValue(v);
              onChange(preserveImageDimensions(v));
            }}
            placeholder={placeholder}
            style={{ minHeight }}
            spellCheck={false}
          />
        </div>
      ) : (
        <div className="rich-editor-wrapper" style={{ minHeight }}>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value || ''}
            onChange={(content) => onChange(preserveImageDimensions(content))}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
          />
        </div>
      )}
      {showPreview && (
        <div className="rich-editor-preview" ref={previewRef}>
          <div className="rich-editor-preview-label">
            Preview{previewLabel ? `: ${previewLabel}` : ''}
          </div>
          {previewLabel && (
            <div className="rich-editor-preview-title">{previewLabel}</div>
          )}
          <div
            key={previewKey}
            className="rich-editor-preview-content"
            dangerouslySetInnerHTML={{ __html: displayValue || '<p><em>No content</em></p>' }}
          />
        </div>
      )}
    </div>
  );
}
